import {
  Alert,
  AlertIcon,
  Box,
  Code,
  Badge,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  VisuallyHidden,
} from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiEdit, FiEye, FiExternalLink, FiPlay } from 'react-icons/fi'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig, FilterConfig } from '@/components/common/DataTable/types'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import { ChainBadge } from '@/components/common/ChainBadge'
import { StatusBadge } from '@/components/common/StatusBadge'
import { TokenAmountDisplay } from '@/components/common/TokenAmountDisplay'
import { InlineSkeleton, PageSkeleton } from '@/components/common/LoadingSkeleton'
import type { ContractRegistryRecord } from '@/types'
import { getAllChains, getExplorerAddressUrl } from '@/constants/chains'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { contractService } from '@/services/admin/contractService'

interface RegistryTableTabProps {
  data?: ContractRegistryRecord[] | null
  isLoading: boolean
  error: unknown
  isFetching: boolean
  onRefresh: () => void
  onSelectStatusUpdate: (record: ContractRegistryRecord) => void
  onSelectDeploy: (record: ContractRegistryRecord) => void
  onSelectDetails: (record: ContractRegistryRecord) => void
  isDeploying: boolean
  deployingRecordId: string | number | null
  getStatusDisplayName: (record: ContractRegistryRecord) => string
  getFormattedAmount: (record: ContractRegistryRecord) => { amountText: string; symbol: string }
  getUserPaymentsInfo: (record: ContractRegistryRecord) => { count: number; isEmpty: boolean }
  formatLastSyncTime: (value?: string) => string
  copyToClipboard: (text: string) => void
  resolveRegistryChainId: (record?: ContractRegistryRecord | null) => string
}

export const RegistryTableTab = ({
  data,
  isLoading,
  error,
  isFetching,
  onRefresh,
  onSelectStatusUpdate,
  onSelectDeploy,
  onSelectDetails,
  isDeploying,
  deployingRecordId,
  getStatusDisplayName,
  getFormattedAmount,
  getUserPaymentsInfo,
  formatLastSyncTime,
  copyToClipboard,
  resolveRegistryChainId,
}: RegistryTableTabProps) => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const { success: showToastSuccess, error: showToastError } = useToast()
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400')
  const copyableCodeStyles = useCopyableCodeStyles()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [chainId, setChainId] = useState('')
  const [merchantId, setMerchantId] = useState('')

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return { data: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } }
    }

    let filtered = [...data]

    if (search.trim()) {
      const searchLower = search.toLowerCase()
      const searchFiltered = filtered.filter(record => {
        if (!record) return false
        const orderMatches =
          record.order_id?.toLowerCase().includes(searchLower) || record.orderId?.toLowerCase().includes(searchLower)
        const merchantBusinessId = (record as any).merchant_business_id
        const merchantBusinessMatch = typeof merchantBusinessId === 'string' && merchantBusinessId.toLowerCase().includes(searchLower)
        const merchantMatches =
          record.merchant_id?.toLowerCase().includes(searchLower) ||
          record.merchant_name?.toLowerCase().includes(searchLower) ||
          merchantBusinessMatch
        const addressMatches = record.payment_address?.toLowerCase().includes(searchLower)
        const statusMatches =
          record.status_id?.toLowerCase().includes(searchLower) || record.status_name?.toLowerCase().includes(searchLower)
        return Boolean(orderMatches || merchantMatches || addressMatches || statusMatches)
      })
      filtered = Array.isArray(searchFiltered) ? searchFiltered : []
    }

    if (status) {
      const statusFiltered = filtered.filter(record => record && (record.status_id || record.status) === status)
      filtered = Array.isArray(statusFiltered) ? statusFiltered : []
    }

    if (chainId) {
      const normalizedChain = chainId.toLowerCase()
      const chainFiltered = filtered.filter(record => {
        if (!record) return false
        const value = resolveRegistryChainId(record)
        return value?.toString().toLowerCase().includes(normalizedChain)
      })
      filtered = Array.isArray(chainFiltered) ? chainFiltered : []
    }

    if (merchantId.trim()) {
      const merchantIdLower = merchantId.toLowerCase()
      const merchantFiltered = filtered.filter(record => {
        if (!record) return false
        const candidateValues: string[] = []
        if (record.merchant_id) {
          candidateValues.push(record.merchant_id)
        }
        const merchantBusinessId = (record as any).merchant_business_id
        if (typeof merchantBusinessId === 'string') {
          candidateValues.push(merchantBusinessId)
        }
        if (record.merchant_name) {
          candidateValues.push(record.merchant_name)
        }
        return candidateValues.some(value => value.toLowerCase().includes(merchantIdLower))
      })
      filtered = Array.isArray(merchantFiltered) ? merchantFiltered : []
    }

    if (!Array.isArray(filtered)) {
      filtered = []
    }

    const total = filtered.length
    const pages = Math.ceil(total / limit)
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    const paginatedData = filtered.slice(startIndex, endIndex)

    return {
      data: paginatedData,
      pagination: {
        page,
        limit,
        total,
        pages,
      },
    }
  }, [data, search, status, chainId, merchantId, page, limit, resolveRegistryChainId])

  const splitOrderId = useCallback((id?: string | null) => {
    if (!id) {
      return []
    }
    const trimmed = id.trim()
    if (!trimmed) {
      return []
    }
    if (trimmed.length <= 18) {
      return [trimmed]
    }
    const maxSegments = 3
    const segmentLength = Math.ceil(trimmed.length / maxSegments)
    const segments: string[] = []
    for (let i = 0; i < maxSegments; i += 1) {
      const start = i * segmentLength
      const part = trimmed.slice(start, start + segmentLength)
      if (part) {
        segments.push(part)
      }
    }
    return segments
  }, [])

  const registryFilterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        key: 'search',
        type: 'text',
        label: 'Search',
        placeholder: t('contractRegistry.searchPlaceholder'),
        width: '250px',
      },
      {
        key: 'status',
        type: 'select',
        label: 'Status',
        placeholder: t('contractRegistry.filterByStatus'),
        width: '160px',
        options: [
          { value: 'orrs_active', label: t('contractRegistry.status.active') },
          { value: 'orrs_on_hold', label: t('contractRegistry.status.on_hold') },
          { value: 'orrs_deployed', label: t('contractRegistry.status.deployed') },
          { value: 'orrs_cancelled', label: t('contractRegistry.status.cancelled') },
        ],
      },
      {
        key: 'chainId',
        type: 'select',
        label: 'Chain',
        placeholder: t('contractRegistry.filterByChain'),
        width: '140px',
        options: getAllChains().map(chain => ({
          value: `chn_01j5qm8n4x7p2r9s8t5v3w4y_${chain.chainId}`,
          label: chain.name,
        })),
      },
      {
        key: 'merchantId',
        type: 'text',
        label: 'Merchant',
        placeholder: t('contractRegistry.filterByMerchant'),
        width: '200px',
      },
    ],
    [t]
  )

  const handleRegistryFiltersChange = useCallback((newFilters: Record<string, any>) => {
    setSearch(newFilters.search || '')
    setStatus(newFilters.status || '')
    setChainId(newFilters.chainId || '')
    setMerchantId(newFilters.merchantId || '')
    setPage(1)
  }, [])

  const handleRegistryPageSizeChange = useCallback((newPageSize: number) => {
    setLimit(newPageSize)
    setPage(1)
  }, [])

  const registryPaginationConfig = filteredData?.pagination
    ? {
        page,
        pageSize: limit,
        total: filteredData.pagination.total,
        pageSizeOptions: [10, 20, 50, 100],
      }
    : undefined

  const handleExport = useCallback(async () => {
    try {
      const { blob, filename } = await contractService.exportRegistryCsv({
        search,
        status,
        chain_id: chainId,
        merchant_id: merchantId,
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      showToastSuccess({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      showToastError({
        title: t('common.exportCsv'),
        description: error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }, [chainId, currentEnv, merchantId, search, showToastError, showToastSuccess, status, t])

  const registryColumns: ColumnConfig<ContractRegistryRecord>[] = useMemo(
    () => [
      {
        key: 'order_id',
        title: t('contractRegistry.table.orderId'),
        width: '18%',
        sortable: true,
        render: (_, record) => {
          const primaryId = record.orderId || record.order_id
          const primaryLines = splitOrderId(primaryId)
          const secondaryId = record.orderId && record.order_id && record.orderId !== record.order_id ? record.order_id : null
          return (
            <Box minH="44px" display="flex" flexDirection="column" justifyContent="center" gap={1}>
              <Tooltip label={primaryId ? t('common.copy') : ''}>
                <Code
                  as="div"
                  bg="transparent"
                  fontSize="xs"
                  whiteSpace="pre-line"
                  wordBreak="break-word"
                  cursor={primaryId ? 'pointer' : 'default'}
                  _hover={primaryId ? copyableCodeStyles.hover : undefined}
                  _active={primaryId ? copyableCodeStyles.active : undefined}
                  onClick={() => primaryId && copyToClipboard(primaryId)}
                >
                  {primaryLines.length > 0 ? primaryLines.join('\n') : t('common.unknown')}
                </Code>
              </Tooltip>
              {secondaryId && (
                <Text
                  fontSize="xs"
                  color={secondaryTextColor}
                  wordBreak="break-word"
                  whiteSpace="pre-wrap"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {secondaryId}
                </Text>
              )}
            </Box>
          )
        },
      },
      {
        key: 'merchant_name',
        title: t('contractRegistry.table.merchant'),
        width: '12%',
        sortable: true,
        render: value => (
          <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
            {value || t('common.unknown')}
          </Text>
        ),
      },
      {
        key: 'status_id',
        title: t('contractRegistry.table.status'),
        width: '10%',
        sortable: true,
        render: (_, record) => (
          <StatusBadge
            statusType="registry"
            status={record.status_id || record.status}
            label={getStatusDisplayName(record)}
          />
        ),
      },
      {
        key: 'chain_id',
        title: t('contractRegistry.table.chain'),
        width: '10%',
        sortable: true,
        render: (_, record) => <ChainBadge chainId={resolveRegistryChainId(record) || 'unknown'} />,
      },
      {
        key: 'amount',
        title: t('contractRegistry.table.amount'),
        width: '10%',
        sortable: true,
        render: (_, record) => {
          const { amountText, symbol } = getFormattedAmount(record)
          return <TokenAmountDisplay amount={amountText} symbol={symbol} />
        },
      },
      {
        key: 'payments_count',
        title: t('contractRegistry.table.userPayments'),
        width: '10%',
        render: (_, record) => {
          const paymentsInfo = getUserPaymentsInfo(record)
          return (
            <Badge size="xs" colorScheme={paymentsInfo.isEmpty ? 'gray' : 'green'}>
              {paymentsInfo.count ?? 0}
            </Badge>
          )
        },
      },
      {
        key: 'updated_at',
        title: t('contractRegistry.table.lastSync'),
        width: '12%',
        sortable: true,
        render: value => (
          <Text fontSize="xs" color={secondaryTextColor}>
            {formatLastSyncTime(value)}
          </Text>
        ),
      },
      {
        key: 'payment_address',
        title: t('contractRegistry.table.paymentAddress', 'Payment Address'),
        width: '18%',
        render: (_, record) => record.payment_address ? (
          <HStack spacing={1} maxW="100%">
            <Code fontSize="xs" flex="1" minW="220px" noOfLines={1} title={record.payment_address}>
              {record.payment_address}
            </Code>
            <Tooltip label={t('contractRegistry.actions.copyAddress')}>
              <IconButton
                aria-label="Copy"
                icon={<FiCopy />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(record.payment_address!)}
              />
            </Tooltip>
            <Tooltip label={t('contractRegistry.actions.viewOnExplorer')}>
              <IconButton
                aria-label="Explorer"
                icon={<FiExternalLink />}
                size="xs"
                variant="ghost"
                onClick={() => {
                  const explorerUrl = getExplorerAddressUrl(resolveRegistryChainId(record) || 'unknown', record.payment_address!)
                  if (explorerUrl) window.open(explorerUrl, '_blank')
                }}
              />
            </Tooltip>
          </HStack>
        ) : <Text fontSize="xs">{t('common.na')}</Text>,
      },
    ],
    [
      copyToClipboard,
      copyableCodeStyles.active,
      copyableCodeStyles.hover,
      formatLastSyncTime,
      getFormattedAmount,
      getStatusDisplayName,
      getUserPaymentsInfo,
      resolveRegistryChainId,
      secondaryTextColor,
      splitOrderId,
      t,
    ]
  )

  const getRegistryRowActions = useCallback(
    (record: ContractRegistryRecord): ActionButton[] => {
      const actions: ActionButton[] = []
      const statusId = record.status_id || record.status
      const isCurrentlyDeploying = isDeploying && deployingRecordId === record.id

      actions.push({
        label: t('contractRegistry.actions.viewDetails'),
        onClick: () => onSelectDetails(record),
        size: 'xs',
        variant: 'ghost',
        icon: <FiEye />,
        tooltip: t('contractRegistry.actions.viewDetails'),
        iconOnly: true,
        ariaLabel: t('contractRegistry.actions.viewDetails'),
      })

      if (statusId !== 'orrs_deployed') {
        actions.push({
          label: t('contractRegistry.actions.updateStatus'),
          onClick: () => onSelectStatusUpdate(record),
          size: 'xs',
          variant: 'ghost',
          icon: <FiEdit />,
          tooltip: t('contractRegistry.actions.updateStatus'),
          iconOnly: true,
          ariaLabel: t('contractRegistry.actions.updateStatus'),
        })
      }

      if (statusId === 'orrs_active') {
        actions.push({
          label: t('contractRegistry.actions.deploy'),
          onClick: () => onSelectDeploy(record),
          size: 'xs',
          variant: 'ghost',
          icon: isCurrentlyDeploying ? <InlineSkeleton isCircle width="12px" height="12px" /> : <FiPlay />,
          tooltip: isCurrentlyDeploying ? t('contractRegistry.actions.deploying') : t('contractRegistry.actions.deployTooltipSmart'),
          loading: isCurrentlyDeploying,
          disabled: isDeploying,
          iconOnly: true,
          ariaLabel: t('contractRegistry.actions.deploy'),
        })
      }

      return actions
    },
    [deployingRecordId, isDeploying, onSelectDeploy, onSelectDetails, onSelectStatusUpdate, t]
  )

  if (isLoading) {
    return (
      <Box role="status" aria-busy="true">
        <PageSkeleton minHeight="360px" cardCount={3} />
        <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {t('common.error')} - {t('contractRegistry.failedToLoad')}
      </Alert>
    )
  }

  return (
    <Box>
      <DataTable
        data={filteredData?.data || []}
        loading={isLoading}
        error={error as Error | null | undefined}
        columns={registryColumns}
        filters={registryFilterConfigs}
        onFiltersChange={handleRegistryFiltersChange}
        showFilterBar
        sortable
        pagination={registryPaginationConfig}
        onPageChange={setPage}
        onPageSizeChange={handleRegistryPageSizeChange}
        actions={getRegistryRowActions}
        actionsColumnWidth="6%"
        size="sm"
        emptyMessage={t('contractRegistry.noRecordsFound')}
        showRefresh
        onRefresh={onRefresh}
        refreshing={isFetching}
        showExport
        onExport={handleExport}
        mobileBreakpoint="md"
      />
    </Box>
  )
}
